<script setup lang="ts">
  import { computed } from 'vue';

  import { type Call } from '@bv/stores';
  import { sip } from '@bv/utils';

  import AnswerAction from './answer/AnswerAction.vue';
  import HangupAction from './hangup/HangupAction.vue';
  import HoldAction from './hold/HoldAction.vue';
  import MuteAction from './mute/MuteAction.vue';
  import SwapAction from './swap/SwapAction.vue';
  import TransferAction from './transfer/TransferAction.vue';

  const props = withDefaults(
    defineProps<{
      call: Call;
      previousCall?: Call;
      size?: 'sm';
      showTransfer?: boolean;
    }>(),
    {
      previousCall: undefined,
      size: undefined,
      showTransfer: false,
    },
  );

  const canMute = computed<boolean>(() => !props.call?.isListen);
  const canHold = computed<boolean>(() => !props.call?.isListen || !props.call?.isListenAndWhisper);

  const emit = defineEmits<{
    (event: 'on-transfer'): void;
  }>();

  /**
   * Emits an 'on-transfer' event.
   * This function notifies a parent component that the user has initiated a transfer action.
   * @returns {void}
   */
  const transfer = (): void => {
    emit('on-transfer');
  };
</script>

<template>
  <div
    v-if="call"
    class="flex flex-wrap justify-center gap-4 [&_.call-action]:h-14 [&_.call-action]:w-14 [&_.call-action>*]:h-6 [&_.call-action>*]:w-6 [&.call-actions-sm_.call-action]:h-10 [&.call-actions-sm_.call-action]:w-10 [&.call-actions-sm_.call-action>*]:h-5 [&.call-actions-sm_.call-action>*]:w-5"
    :class="{ 'call-actions-sm': size === 'sm' }"
    tabindex="1"
  >
    <template v-if="call.status === 'Established'">
      <transfer-action
        v-if="showTransfer"
        @click="transfer"
      />
      <mute-action
        v-if="canMute"
        :is-mute="!!call.isMute"
        @click="!call.isMute ? sip.mute(call) : sip.unmute(call)"
      />
      <hold-action
        v-if="canHold"
        :is-hold="!!call.isHold"
        @click="!call.isHold ? sip.hold(call) : sip.resume(call)"
      />
      <swap-action
        v-if="previousCall"
        @click="sip.activateCall(previousCall)"
      />
    </template>
    <hangup-action @click="sip.hangup(call)" />
    <answer-action
      v-if="call.status === 'ringing'"
      @click="sip.accept(call)"
    />
  </div>
</template>
